import { useBlocker } from "react-router-dom";
import { Button, Modal } from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectDestination, selectEditJobId, selectPickup, selectStops } from "@/redux/slices/clientBooking/selectors";
import { useBookingViewOnly } from "./BookingViewOnlyContext";

/**
 * Guards the booking page: if an in-progress booking exists, intercepts in-app navigation away and
 * confirms via the "Leave Booking?" modal (Figma). Relies on the data router (createBrowserRouter).
 */
export const LeaveBookingModal = () => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  const pickup = useAppSelector(selectPickup);
  const destination = useAppSelector(selectDestination);
  const stops = useAppSelector(selectStops);
  // Edit mode replays an existing booking (editJobId set) — the guard still applies, but the copy
  // must say "editing", not "creating".
  const isEdit = useAppSelector(selectEditJobId) != null;
  // View-only hydrates a full draft, but there's nothing to lose leaving a closed booking — don't guard.
  const hasDraft = !viewOnly && Boolean(pickup || destination || stops.some(Boolean));

  const description = isEdit
    ? "You're currently editing this booking. If you leave now, your changes won't be saved. Are you sure you want to leave this page?"
    : "You're currently creating a booking. If you leave now, any changes you've made won't be saved. Are you sure you want to leave this page?";

  // Booking success clears the draft and navigates in the same tick, but the blocker closure the
  // router evaluates is re-registered in an effect — it still sees the pre-clear draft. Exempt the
  // confirmation page so the happy path is never intercepted. Matches all four confirmations:
  // client "/client/booking/confirmation", operator ".../clients/:id/booking/confirmation",
  // guest "/guest/booking/:operatorCode/confirmation" (operator code between "booking" and
  // "confirmation"), and EA OBO "/client/book-for/:accessId/confirmation" (DEV-1296).
  const isBookingConfirmation = (path: string) => /\/(booking|book-for)(\/[^/]+)?\/confirmation$/.test(path);
  // The Update-Booking success navigates home (not a confirmation page), so it tags the navigation with
  // `skipBookingLeaveGuard`. Reading it off `nextLocation` (passed fresh to the blocker) sidesteps the
  // stale-closure race above without a global flag.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasDraft &&
      currentLocation.pathname !== nextLocation.pathname &&
      !isBookingConfirmation(nextLocation.pathname) &&
      !(nextLocation.state as { skipBookingLeaveGuard?: boolean } | null)?.skipBookingLeaveGuard
  );

  const reset = () => blocker.state === "blocked" && blocker.reset();
  // Confirmed leave of an edit. Order matters: proceed() FIRST (while still blocked) so the navigation
  // commits, THEN clear — clearing first flips `hasDraft` false and resets the blocker mid-flight. The
  // clear lands in the same React batch, so the sheet remounts (keyed on jobId) with an already-empty
  // draft, before LocationPanel seeds its address inputs from it. (Create drafts are left intact for resume.)
  const leave = () => {
    if (blocker.state !== "blocked") return;
    blocker.proceed();
    if (isEdit) dispatch(clearBooking());
  };

  return (
    <Modal
      open={blocker.state === "blocked"}
      onClose={reset}
      variant="web-small"
      title="Leave Booking?"
      description={description}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={reset}>
            Don&apos;t Leave
          </Button>
          <Button variant="custom" size="md" className="bg-danger text-white hover:bg-danger/90" onClick={leave}>
            Leave Booking
          </Button>
        </>
      )}
    />
  );
};
